import React, { useCallback, useEffect, useMemo } from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { addQuery, QueryVal } from 'Helpers/Query';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { TIMEFRAME_QP } from 'Organisms/ScenarioChart';

const TAB_QP = 'tab';

const TabContentContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';
const SET_ACTIVE_TAB_RESOURCE_TYPE = 'set-active-tab-resource-type';
const SET_ACTIVE_TAB_RESOURCE = 'set-active-tab-resource';

const init = () => {
  const tabQueryParam = QueryVal(TAB_QP) || fr.relationships.outcomeSummary.name;

  return {
    isInitializing: true,
    initializingError: null,
    isFacetPlanOutcomeSummaryViewRefreshing: false,
    facetPlanOutcomeSummaryViewRefreshError: null,
    activeTabResourceType: tabQueryParam,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case SET_ACTIVE_TAB_RESOURCE_TYPE: {
      return {
        ...state,
        activeTabResourceType: action.activeTabResourceType,
      };
    }
    case SET_ACTIVE_TAB_RESOURCE: {
      return {
        ...state,
        activeTabResource: action.activeTabResource,
        // activeTabResourceType: action.activeTabResource.type(), // Not doing this because there's a use effect somewhere that changes the tab and forces a page refresh
      };
    }
    default: {
      throw new Error(
        `Unhandled TabContentContext action type: ${action.type}`,
      );
    }
  }
};

const TabContentProvider = ({ children }) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { pageContentResource, pageContentCollection } = usePageContentContext();
  const [state, dispatch] = React.useReducer(reducer, null, init);

  // ----------------- LOADING / SETTING TAB RESOURCES  -----------------

  const setActiveTabResourceType = React.useCallback(
    activeTabResourceType => {
      if (QueryVal(TAB_QP) !== activeTabResourceType) {
        // Set Active tab
        addQuery({
          [TAB_QP]: activeTabResourceType,
        });
      }


      dispatch({
        type: SET_ACTIVE_TAB_RESOURCE_TYPE,
        activeTabResourceType,
      });

      logStandardizedEvent(categories.whatIf, eventTypes.navigateTo, 'scenario content tab', {
        eventProperties: {
          scenarioId: pageContentResource.id(),
          resourceType: activeTabResourceType,
        },
      });
    },
    [state],
  );

  const setActiveTabResource = useCallback(activeTabResource => {
    dispatch({
      type: SET_ACTIVE_TAB_RESOURCE,
      activeTabResource,
    });
  }, []);


  const getActiveTabResource = () => {
    if (state.activeTabResource) {
      return state.activeTabResource;
    }

    if (state.activeTabResourceType === null || state.activeTabResourceType === undefined) {
      return null;
    }

    // Get the relationship for the active tab
    const relationship = pageContentResource.relationship(state.activeTabResourceType);

    // Set active tab to outcome summary if the relationship is not found
    if (!relationship) {
      setActiveTabResourceType(fr.relationships.outcomeSummary.name);
      return null;
    }

    return pageContentCollection.included().findResource(relationship?.data[0]);
  };

  useEffect(() => {
    setActiveTabResource(undefined);
  }, [pageContentCollection, state.activeTabResourceType]);

  const newActiveTabResource = getActiveTabResource();
  const activeTabResource = useMemo(() => {
    return newActiveTabResource;
  }, [state.activeTabResourceType, newActiveTabResource?.instanceId(), pageContentCollection, pageContentResource]);

  // ----------------- CREATE DETAIL VIEW TABS  -----------------

  const tabs = React.useMemo(() => {
    return pageContentResource
      .attr('tabs')
      .resources()
      .list()
      .map(tab => ({
        key: tab.id(),
        label: tab.attr('label'),
        value: tab.attr('key'),
      }));
  }, [pageContentResource]);

  const tabResources = React.useMemo(() => {
    return tabs.map(tab => {
      // Get the relationship for the active tab
      const relationship = pageContentResource.relationship(tab.value);

      // Set active tab to outcome summary if the relationship is not found
      if (!relationship) {
        return null;
      }

      return pageContentCollection.included().findResource(relationship?.data[0]);
    });
  }, [pageContentCollection, pageContentResource]);


  // Set active tab on mount (utilize tab QP if there is one present)
  useComponentDidMount(() => {
    const tabQueryValue = QueryVal(TAB_QP);
    setActiveTabResourceType(tabQueryValue || tabs[0].value);
  });

  const timeFrameOptions = activeTabResource?.meta()?.queries()?.head()?.field(TIMEFRAME_QP)
    ?.options()
    .list()
    .map(option => ({
      name: option.attr('prompt'),
      value: option.attr('id'),
    }));

  const memoizedValue = React.useMemo(() => {
    return {
      activeTabResource,
      tabResources,
      activeTabResourceType: state.activeTabResourceType,
      initializingError: state.initializingError,
      isInitializing: state.isInitializing,
      setActiveTabResource,
      setActiveTabResourceType,
      tabs,
      timeFrameOptions,
    };
  }, [activeTabResource, setActiveTabResource, state, tabs]);

  return (
    <TabContentContext.Provider value={memoizedValue}>
      {children}
    </TabContentContext.Provider>
  );
};

// Use TabContent State Context
const useTabContentContext = () => {
  const context = React.useContext(TabContentContext);
  if (context === undefined) {
    throw new Error('useTabContentContext must be used within a TabContentProvider');
  }
  return context;
};

TabContentProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  TabContentProvider,
  useTabContentContext,
};
